import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, 
  Bell, 
  Mail, 
  EyeOff, 
  AlertTriangle, 
  Palette,
  Settings,
  Zap,
  Database,
  BarChart3,
  Download,
  Upload,
  Trash2,
  Users,
  FileText,
  BookOpen,
  MessageSquare,
  XCircle,
  CheckCircle,
  RefreshCw,
  Save,
  HardDrive,
  Sparkles,
} from 'lucide-react';
import CacheManagement from '../../components/admin/CacheManagement';
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  writeBatch
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { performanceMonitor } from '../../firebase/config';
import { saveAdminSetting, getAdminSetting } from '../../firebase/collections';
import { uploadFile, generateFilePath, validateFile, formatFileSize } from '../../firebase/r2storage';

const SystemSettings = () => {
  const [settings, setSettings] = useState({
    siteName: 'Mahstry',
    siteDescription: 'Mock Test Platform',
    maintenanceMode: false,
    registrationEnabled: true,
    emailVerification: true,
    passwordMinLength: 8,
    requireSpecialChars: true,
    sessionTimeout: 30,
    maxLoginAttempts: 5,
    enableTwoFactor: false,
    cacheEnabled: true,
    cacheDuration: 300,
    imageOptimization: true,
    lazyLoading: true,
    compressionEnabled: true,
    emailNotifications: true,
    pushNotifications: true,
    adminAlerts: true,
    userAlerts: true,
    autoBackup: true,
    backupFrequency: 'daily',
    backupRetention: 30,
    cloudBackup: false,
    googleAnalytics: false,
    analyticsId: '',
    trackingEnabled: true,
    privacyMode: false,
    defaultTheme: 'light',
    allowUserTheme: true,
    primaryColor: '#3B82F6',
    accentColor: '#10B981',
    aiTutorEnabled: false,
    joinAsOnboardingVideoUrl: '',
    joinAsOnboardingVideoEnabled: true,
    // Payment Settings
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [showPassword, setShowPassword] = useState(false);
  const [systemStats, setSystemStats] = useState({});
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoUploadError, setVideoUploadError] = useState('');

  // Load settings from Firebase
  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    
    try {
      performanceMonitor.start('load_system_settings');
      
      const settingsDoc = await getDoc(doc(db, 'systemSettings', 'main'));
      if (settingsDoc.exists()) {
        setSettings(prev => ({ ...prev, ...settingsDoc.data() }));
      }
      
      // Load AI Tutor setting from adminSettings
      const aiTutorResult = await getAdminSetting('aiTutorEnabled');
      if (aiTutorResult.success && aiTutorResult.data) {
        setSettings(prev => ({ ...prev, aiTutorEnabled: aiTutorResult.data.value === true }));
      }
      
      await loadSystemStats();
      
      performanceMonitor.end('load_system_settings');
      setSuccess('Settings loaded successfully');
    } catch (err) {
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  // Save settings to Firebase
  const saveSettings = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      performanceMonitor.start('save_system_settings');
      
      // Save main settings
      const { aiTutorEnabled, ...mainSettings } = settings;
      await setDoc(doc(db, 'systemSettings', 'main'), mainSettings, { merge: true });
      
      // Save AI Tutor setting separately to adminSettings
      await saveAdminSetting({
        key: 'aiTutorEnabled',
        value: aiTutorEnabled,
        label: 'AI Tutor Enabled',
        description: 'Enable or disable the AI Tutor feature for all users',
        type: 'boolean',
        updatedAt: new Date().toISOString()
      });
      
      await logAdminAction('settings_updated', {
        timestamp: new Date().toISOString(),
        changes: Object.keys(settings)
      });
      
      performanceMonitor.end('save_system_settings');
      setSuccess('Settings saved successfully');
    } catch (err) {
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleJoinVideoUpload = async (file) => {
    if (!file) return;
    setVideoUploadError('');

    const validation = validateFile(file, {
      maxSize: 200 * 1024 * 1024, // 200MB cap for intro video
      allowedTypes: ['video/*']
    });

    if (!validation.isValid) {
      setVideoUploadError(validation.errors.join(', '));
      return;
    }

    setVideoUploading(true);
    try {
      const path = generateFilePath(file.name, 'system/joinOnboardingVideos');
      const uploadResult = await uploadFile(file, path);
      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'Upload failed');
      }
      setSettings(prev => ({ ...prev, joinAsOnboardingVideoUrl: uploadResult.url }));
      setSuccess('Video uploaded successfully');
    } catch (error) {
      setVideoUploadError(error.message || 'Failed to upload video');
    } finally {
      setVideoUploading(false);
    }
  };

  // Load system statistics
  const loadSystemStats = async () => {
    try {
      const collections = ['users', 'testResults', 'mockTests', 'questionBank', 'notes', 'doubts'];
      const stats = {};
      
      await Promise.all(
        collections.map(async (collectionName) => {
          try {
            const snapshot = await getDocs(collection(db, collectionName));
            stats[collectionName] = snapshot.size;
          } catch (error) {
            stats[collectionName] = 0;
          }
        })
      );
      
      stats.totalUsers = stats.users || 0;
      stats.totalTests = stats.testResults || 0;
      stats.totalQuestions = stats.questionBank || 0;
      stats.totalNotes = stats.notes || 0;
      stats.totalDoubts = stats.doubts || 0;
      
      setSystemStats(stats);
    } catch (error) {
      }
  };

  // Log admin action
  const logAdminAction = async (action, details) => {
    try {
      await setDoc(doc(collection(db, 'adminActions')), {
        action,
        details,
        timestamp: new Date().toISOString(),
        adminId: 'system-admin'
      });
    } catch (error) {
      }
  };

  // Reset settings to defaults
  const resetSettings = async () => {
    if (window.confirm('Are you sure you want to reset all settings to defaults? This action cannot be undone.')) {
      setSettings({
        siteName: 'Mahstry',
        siteDescription: 'Mock Test Platform',
        maintenanceMode: false,
        registrationEnabled: true,
        emailVerification: true,
        passwordMinLength: 8,
        requireSpecialChars: true,
        sessionTimeout: 30,
        maxLoginAttempts: 5,
        enableTwoFactor: false,
        cacheEnabled: true,
        cacheDuration: 300,
        imageOptimization: true,
        lazyLoading: true,
        compressionEnabled: true,
        emailNotifications: true,
        pushNotifications: true,
        adminAlerts: true,
        userAlerts: true,
        autoBackup: true,
        backupFrequency: 'daily',
        backupRetention: 30,
        cloudBackup: false,
        googleAnalytics: false,
        analyticsId: '',
        trackingEnabled: true,
        privacyMode: false,
        defaultTheme: 'light',
        allowUserTheme: true,
        primaryColor: '#3B82F6',
        accentColor: '#10B981',
        aiTutorEnabled: false,
        joinAsOnboardingVideoUrl: '',
        joinAsOnboardingVideoEnabled: true,
      });
      setSuccess('Settings reset to defaults');
    }
  };

  // Export settings
  const exportSettings = () => {
    const dataStr = JSON.stringify(settings, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'mahstry-settings.json';
    link.click();
    URL.revokeObjectURL(url);
    setSuccess('Settings exported successfully');
  };

  // Import settings
  const importSettings = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const importedSettings = JSON.parse(e.target.result);
          setSettings(prev => ({ ...prev, ...importedSettings }));
          setSuccess('Settings imported successfully');
        } catch (error) {
          setError('Invalid settings file');
        }
      };
      reader.readAsText(file);
    }
  };

  // Handle setting change
  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const tabs = [
    { id: 'general', name: 'General', icon: Settings },
    { id: 'security', name: 'Security', icon: Shield },
    { id: 'performance', name: 'Performance', icon: Zap },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'backup', name: 'Backup', icon: Database },
    { id: 'analytics', name: 'Analytics', icon: BarChart3 },
    { id: 'theme', name: 'Theme', icon: Palette },
    { id: 'cache', name: 'Cache Management', icon: HardDrive }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg mr-4">
                <Settings className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">System Settings</h1>
                <p className="text-gray-600 mt-1">Configure system preferences and behavior</p>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={exportSettings}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </button>
              <label className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center cursor-pointer">
                <Upload className="h-4 w-4 mr-2" />
                Import
                <input
                  type="file"
                  accept=".json"
                  onChange={importSettings}
                  className="hidden"
                />
              </label>
              <button
                onClick={resetSettings}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Reset
              </button>
            </div>
          </div>

          {/* System Statistics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center">
                <Users className="h-5 w-5 text-blue-600 mr-2" />
                <div>
                  <p className="text-sm text-blue-600">Total Users</p>
                  <p className="text-lg font-semibold text-blue-900">{systemStats.totalUsers || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center">
                <FileText className="h-5 w-5 text-green-600 mr-2" />
                <div>
                  <p className="text-sm text-green-600">Total Tests</p>
                  <p className="text-lg font-semibold text-green-900">{systemStats.totalTests || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="flex items-center">
                <BookOpen className="h-5 w-5 text-purple-600 mr-2" />
                <div>
                  <p className="text-sm text-purple-600">Questions</p>
                  <p className="text-lg font-semibold text-purple-900">{systemStats.totalQuestions || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <div className="flex items-center">
                <MessageSquare className="h-5 w-5 text-orange-600 mr-2" />
                <div>
                  <p className="text-sm text-orange-600">Doubts</p>
                  <p className="text-lg font-semibold text-orange-900">{systemStats.totalDoubts || 0}</p>
                </div>
              </div>
            </div>
          </div>


          {/* Status Messages */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <XCircle className="h-5 w-5 text-red-600 mr-2" />
                <span className="text-red-800">{error}</span>
              </div>
            </div>
          )}
          
          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-green-800">{success}</span>
              </div>
            </div>
          )}
        </div>

        {/* Settings Tabs */}
        <div className="bg-white rounded-lg shadow-lg">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-8">
            {activeTab === 'general' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">General Settings</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Site Name
                    </label>
                    <input
                      type="text"
                      value={settings.siteName}
                      onChange={(e) => handleSettingChange('siteName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Site Description
                    </label>
                    <input
                      type="text"
                      value={settings.siteDescription}
                      onChange={(e) => handleSettingChange('siteDescription', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="maintenanceMode"
                      checked={settings.maintenanceMode}
                      onChange={(e) => handleSettingChange('maintenanceMode', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="maintenanceMode" className="ml-2 block text-sm text-gray-900">
                      Maintenance Mode
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="registrationEnabled"
                      checked={settings.registrationEnabled}
                      onChange={(e) => handleSettingChange('registrationEnabled', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="registrationEnabled" className="ml-2 block text-sm text-gray-900">
                      Enable Registration
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="emailVerification"
                      checked={settings.emailVerification}
                      onChange={(e) => handleSettingChange('emailVerification', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="emailVerification" className="ml-2 block text-sm text-gray-900">
                      Require Email Verification
                    </label>
                  </div>
                  
                  <div className="flex items-center md:col-span-2">
                    <input
                      type="checkbox"
                      id="aiTutorEnabled"
                      checked={settings.aiTutorEnabled}
                      onChange={(e) => handleSettingChange('aiTutorEnabled', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="aiTutorEnabled" className="ml-2 block text-sm text-gray-900 flex items-center">
                      <Sparkles className="h-4 w-4 mr-2 text-purple-600" />
                      <span>
                        <strong>Enable AI Tutor Feature</strong>
                        <span className="block text-xs text-gray-500 mt-1">
                          Allow users to access the AI Tutor for solving doubts, doubt classes, PTM, introduction sessions, test analysis live classes, and more
                        </span>
                      </span>
                    </label>
                  </div>

                  <div className="md:col-span-2 border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Join-as Onboarding Video</h4>
                        <p className="text-xs text-gray-500 mt-1">
                          Video shown in fullscreen before users reach the School/Coaching/College onboarding page.
                        </p>
                      </div>
                      <label className="inline-flex items-center space-x-2">
                        <span className="text-sm text-gray-700">Enable</span>
                        <input
                          type="checkbox"
                          checked={settings.joinAsOnboardingVideoEnabled}
                          onChange={(e) => handleSettingChange('joinAsOnboardingVideoEnabled', e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </label>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Upload Video (mp4 / streaming)
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="file"
                          accept="video/*"
                          onChange={(e) => handleJoinVideoUpload(e.target.files?.[0])}
                          disabled={videoUploading}
                          className="text-sm"
                        />
                        {videoUploading && (
                          <span className="text-xs text-gray-500">Uploading...</span>
                        )}
                      </div>

                      {settings.joinAsOnboardingVideoUrl && (
                        <div className="text-xs text-green-700 bg-green-50 border border-green-100 rounded p-2">
                          Video ready. Current link: {settings.joinAsOnboardingVideoUrl}
                        </div>
                      )}

                      {videoUploadError && (
                        <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded p-2">
                          {videoUploadError}
                        </div>
                      )}

                      <p className="text-xs text-gray-500">
                        This plays fullscreen when someone clicks “Join as School/Coaching/College”. Users can skip or will be redirected after the video ends. Max size 200MB.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'security' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Security Settings</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Minimum Password Length
                    </label>
                    <input
                      type="number"
                      min="6"
                      max="20"
                      value={settings.passwordMinLength}
                      onChange={(e) => handleSettingChange('passwordMinLength', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Session Timeout (minutes)
                    </label>
                    <input
                      type="number"
                      min="5"
                      max="1440"
                      value={settings.sessionTimeout}
                      onChange={(e) => handleSettingChange('sessionTimeout', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Login Attempts
                    </label>
                    <input
                      type="number"
                      min="3"
                      max="10"
                      value={settings.maxLoginAttempts}
                      onChange={(e) => handleSettingChange('maxLoginAttempts', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="requireSpecialChars"
                      checked={settings.requireSpecialChars}
                      onChange={(e) => handleSettingChange('requireSpecialChars', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="requireSpecialChars" className="ml-2 block text-sm text-gray-900">
                      Require Special Characters in Passwords
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="enableTwoFactor"
                      checked={settings.enableTwoFactor}
                      onChange={(e) => handleSettingChange('enableTwoFactor', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="enableTwoFactor" className="ml-2 block text-sm text-gray-900">
                      Enable Two-Factor Authentication
                    </label>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'performance' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Performance Settings</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="cacheEnabled"
                      checked={settings.cacheEnabled}
                      onChange={(e) => handleSettingChange('cacheEnabled', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="cacheEnabled" className="ml-2 block text-sm text-gray-900">
                      Enable Caching
                    </label>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cache Duration (seconds)
                    </label>
                    <input
                      type="number"
                      min="60"
                      max="3600"
                      value={settings.cacheDuration}
                      onChange={(e) => handleSettingChange('cacheDuration', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="imageOptimization"
                      checked={settings.imageOptimization}
                      onChange={(e) => handleSettingChange('imageOptimization', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="imageOptimization" className="ml-2 block text-sm text-gray-900">
                      Enable Image Optimization
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="lazyLoading"
                      checked={settings.lazyLoading}
                      onChange={(e) => handleSettingChange('lazyLoading', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="lazyLoading" className="ml-2 block text-sm text-gray-900">
                      Enable Lazy Loading
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="compressionEnabled"
                      checked={settings.compressionEnabled}
                      onChange={(e) => handleSettingChange('compressionEnabled', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="compressionEnabled" className="ml-2 block text-sm text-gray-900">
                      Enable Compression
                    </label>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Notification Settings</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="emailNotifications"
                      checked={settings.emailNotifications}
                      onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="emailNotifications" className="ml-2 block text-sm text-gray-900">
                      Email Notifications
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="pushNotifications"
                      checked={settings.pushNotifications}
                      onChange={(e) => handleSettingChange('pushNotifications', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="pushNotifications" className="ml-2 block text-sm text-gray-900">
                      Push Notifications
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="adminAlerts"
                      checked={settings.adminAlerts}
                      onChange={(e) => handleSettingChange('adminAlerts', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="adminAlerts" className="ml-2 block text-sm text-gray-900">
                      Admin Alerts
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="userAlerts"
                      checked={settings.userAlerts}
                      onChange={(e) => handleSettingChange('userAlerts', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="userAlerts" className="ml-2 block text-sm text-gray-900">
                      User Alerts
                    </label>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'backup' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Backup Settings</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="autoBackup"
                      checked={settings.autoBackup}
                      onChange={(e) => handleSettingChange('autoBackup', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="autoBackup" className="ml-2 block text-sm text-gray-900">
                      Automatic Backup
                    </label>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Backup Frequency
                    </label>
                    <select
                      value={settings.backupFrequency}
                      onChange={(e) => handleSettingChange('backupFrequency', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="hourly">Hourly</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Backup Retention (days)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={settings.backupRetention}
                      onChange={(e) => handleSettingChange('backupRetention', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="cloudBackup"
                      checked={settings.cloudBackup}
                      onChange={(e) => handleSettingChange('cloudBackup', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="cloudBackup" className="ml-2 block text-sm text-gray-900">
                      Cloud Backup
                    </label>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'analytics' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Analytics Settings</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="googleAnalytics"
                      checked={settings.googleAnalytics}
                      onChange={(e) => handleSettingChange('googleAnalytics', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="googleAnalytics" className="ml-2 block text-sm text-gray-900">
                      Enable Google Analytics
                    </label>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Analytics ID
                    </label>
                    <input
                      type="text"
                      value={settings.analyticsId}
                      onChange={(e) => handleSettingChange('analyticsId', e.target.value)}
                      placeholder="G-XXXXXXXXXX"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="trackingEnabled"
                      checked={settings.trackingEnabled}
                      onChange={(e) => handleSettingChange('trackingEnabled', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="trackingEnabled" className="ml-2 block text-sm text-gray-900">
                      Enable User Tracking
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="privacyMode"
                      checked={settings.privacyMode}
                      onChange={(e) => handleSettingChange('privacyMode', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="privacyMode" className="ml-2 block text-sm text-gray-900">
                      Privacy Mode (GDPR Compliant)
                    </label>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'theme' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Theme Settings</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Default Theme
                    </label>
                    <select
                      value={settings.defaultTheme}
                      onChange={(e) => handleSettingChange('defaultTheme', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="light">Light</option>
                      <option value="dark">Dark</option>
                      <option value="auto">Auto (System)</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="allowUserTheme"
                      checked={settings.allowUserTheme}
                      onChange={(e) => handleSettingChange('allowUserTheme', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="allowUserTheme" className="ml-2 block text-sm text-gray-900">
                      Allow Users to Change Theme
                    </label>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Primary Color
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={settings.primaryColor}
                        onChange={(e) => handleSettingChange('primaryColor', e.target.value)}
                        className="h-10 w-16 border border-gray-300 rounded"
                      />
                      <input
                        type="text"
                        value={settings.primaryColor}
                        onChange={(e) => handleSettingChange('primaryColor', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Accent Color
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={settings.accentColor}
                        onChange={(e) => handleSettingChange('accentColor', e.target.value)}
                        className="h-10 w-16 border border-gray-300 rounded"
                      />
                      <input
                        type="text"
                        value={settings.accentColor}
                        onChange={(e) => handleSettingChange('accentColor', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}


            {activeTab === 'cache' && (
              <div className="space-y-6">
                <CacheManagement />
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="border-t border-gray-200 px-8 py-4">
            <div className="flex justify-end space-x-3">
              <button
                onClick={loadSettings}
                disabled={loading}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Loading...' : 'Reload'}
              </button>
              <button
                onClick={saveSettings}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemSettings;
